import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const mxn = (cents: number) => `$${Math.round(cents / 100).toLocaleString('en-US')}`;
const usd = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { type Pack } from '@/components/pricing/PackCard';
import { CheckoutStrip } from '@/components/trust/CheckoutStrip';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { currentPayJob, rememberPendingJob } from '@/lib/pay-sheet';

// THE PAY SHEET (Studio Blueprint 04: "charge at the moment of wanting").
// Opened over the free preview by a studio's render check when the balance
// is short: what this render needs, what you have, the cheapest pack that
// covers it already picked (any pack: a 1-music-video pack covers a music
// video for less than the next credit size), and the next two cheapest that
// cover it, labelled by how many more renders they buy. Prices come from /api/web/packs (one price file,
// shared/config/packs.js); nothing here changes what a render costs or when
// credits are charged. Checkout returns to the same studio console
// (`back`, routes: services/studio-signin.js checkoutReturns), where the
// page waits for the credits and the studio puts the same job back and
// starts it (components/pay/useResumeJob).

export const PAY_SHEET_EVENT = 'site:pay-sheet';

export interface PayRequest {
  /** Credits the job costs. */
  need: number;
  /** Credits on the account now. */
  have: number;
  /** What is being paid for, in the flow's words ("Render your music video"). */
  label: string;
}

export function openPaySheet(req: PayRequest) {
  window.dispatchEvent(new CustomEvent<PayRequest>(PAY_SHEET_EVENT, { detail: req }));
}

const BACK: Record<string, 'music' | 'ads' | 'tiktok'> = { '/music': 'music', '/ads': 'ads', '/tiktok': 'tiktok' };

export function PaySheet() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [req, setReq] = useState<PayRequest | null>(null);
  const [packs, setPacks] = useState<Pack[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [buying, setBuying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<PayRequest>).detail;
      if (!detail) return;
      setReq(detail);
      setError(null);
      setChosen(null);
      trackEvent('pay_sheet_opened', { need: detail.need, have: detail.have, page: window.location.pathname });
    };
    window.addEventListener(PAY_SHEET_EVENT, onOpen);
    return () => window.removeEventListener(PAY_SHEET_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!req || packs) return;
    api
      .getPacks()
      .then((list) => setPacks([...list].sort((a, b) => a.price_cents - b.price_cents)))
      .catch(() => setFailed(true));
  }, [req, packs]);

  // The three cheapest packs that cover the shortfall (the biggest pack when
  // none does).
  const offer = useMemo(() => {
    if (!req || !packs?.length) return [];
    const short = Math.max(1, req.need - req.have);
    const covering = packs.filter((p) => p.points >= short);
    return covering.length ? covering.slice(0, 3) : [packs.reduce((a, b) => (b.points > a.points ? b : a))];
  }, [req, packs]);

  const note = useCallback(
    (p: Pack) => {
      if (!req || req.need <= 0) return undefined;
      const renders = Math.floor((req.have + p.points) / req.need);
      return renders <= 1 ? t('pay.coversThis') : t('pay.coversMore', { count: renders - 1 });
    },
    [req, t],
  );

  const buy = async (type: string) => {
    if (!api.getToken()) {
      navigate(`/signup?redirect_to=${encodeURIComponent(location.pathname + location.search + location.hash)}`);
      return;
    }
    setBuying(type);
    setError(null);
    try {
      const back = BACK[location.pathname];
      // The job on screen rides through checkout in this tab, so the studio
      // can put it back and start it when the credits land (lib/pay-sheet).
      const job = back && req ? currentPayJob(back) : null;
      if (back && req) rememberPendingJob({ studio: back, need: req.need, ...(job || {}) });
      const { url } = await api.createCheckout({ pack_type: type, ...(back ? { back } : {}), ...(job?.runId ? { run: job.runId } : {}) });
      trackEvent('checkout_started', { checkout_flow: 'pay_sheet', pack_type: type, surface: location.pathname });
      window.location.href = url;
    } catch (e: unknown) {
      const failure = (e && typeof e === 'object' ? e : {}) as { code?: string; message?: string };
      setError(failure.code || failure.message || t('pay.failed'));
      setBuying(null);
    }
  };

  return (
    <Sheet open={!!req} onOpenChange={(open) => !open && setReq(null)}>
      <SheetContent side="bottom" data-testid="pay-sheet" className="mx-auto max-h-[90dvh] max-w-xl overflow-y-auto rounded-t-3xl border-white/10 bg-[#0b0d12] pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">{req?.label}</SheetTitle>
          <SheetDescription className="tabular-nums">
            {req && t('pay.needHave', { need: req.need.toLocaleString(), have: req.have.toLocaleString() })}
          </SheetDescription>
        </SheetHeader>
        {/* Need vs have, as a bar (blueprint 04's pay sheet). */}
        {req && req.need > 0 && (
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10" aria-hidden>
            <i className="block h-full rounded-full bg-primary" style={{ width: `${Math.min(100, Math.round((req.have / req.need) * 100))}%` }} />
          </div>
        )}
        <p className="mt-3 text-[14px] text-muted-foreground">{t('pay.lead')}</p>
        <div className="mt-4 space-y-2.5" role="radiogroup" aria-label={t('pay.choose')}>
          {failed ? (
            <p className="text-destructive">{t('pay.loadFailed')}</p>
          ) : !packs ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label={t('app.loading')} />
            </div>
          ) : (
            offer.map((p) => {
              const on = (chosen ?? offer[0]?.type) === p.type;
              return (
                <button
                  key={p.type}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  data-testid={`pay-pick-${p.type}`}
                  onClick={() => setChosen(p.type)}
                  className={cn('flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left transition-colors', on ? 'border-primary bg-primary/[0.08]' : 'border-white/10 bg-white/[0.03] hover:border-white/25')}
                >
                  <span aria-hidden className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2', on ? 'border-primary' : 'border-white/30')}>
                    {on && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-[15px] font-bold tabular-nums">{t('pricingPage.creditsCount', { credits: p.points.toLocaleString('en-US') })}</span>
                    <span className="block text-[12.5px] text-muted-foreground">{note(p)}</span>
                  </span>
                  <span className="shrink-0 text-right tabular-nums">
                    <span className="block text-[14px] font-bold">{mxn(p.price_cents)} MXN</span>
                    <span className="block text-[12px] text-muted-foreground">{usd(p.usd_cents)} USD</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
        {error && <p className="mt-3 text-[14px] text-destructive" role="alert">{error}</p>}
        {(() => {
          const pick = offer.find((p) => p.type === (chosen ?? offer[0]?.type));
          if (!pick) return null;
          return (
            <button
              type="button"
              data-testid="pay-go"
              disabled={!!buying}
              onClick={() => void buy(pick.type)}
              className="mt-5 flex min-h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] text-[16px] font-bold tabular-nums text-[#10131a] disabled:opacity-60"
            >
              {buying ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : t('pay.payButton', { mxn: mxn(pick.price_cents), usd: usd(pick.usd_cents) })}
            </button>
          );
        })()}
        <CheckoutStrip methods="packs" className="mt-4" />
        <p className="mt-3 text-center text-[13px] text-muted-foreground">{t('pay.comeBack')}</p>
        <a href="/pricing" className="mt-2 block text-center text-[13px] font-semibold text-primary underline-offset-4 hover:underline">
          {t('pay.allSizes')}
        </a>
      </SheetContent>
    </Sheet>
  );
}
